import { Button } from "@/components/shadcn/button";
import { Videos } from "@prisma/client";
import Vimeo from "@u-wave/react-vimeo";
import { useTranslations } from "next-intl";
import { useRef, useState } from "react";

interface VimeoPlayerProps {
    video: Videos;
    onEnded?: () => void;
    onPlay?: () => void;
    onPause?: () => void;
    seekTime?: number;
    autoPlay?: boolean;
}
export const VimeoPlayer = ({
    video,
    onPlay,
    onEnded,
    onPause,
    seekTime = 0,
    autoPlay = false,
}: VimeoPlayerProps) => {
    const t = useTranslations("series");
    const [currentTimestamp, setCurrentTimestamp] = useState(0);
    // Embed-code fetch can fail client-side (ad blockers blocking vimeo.com,
    // network drops). Without onError this surfaces as an unhandled rejection
    // and the user is left with a dead player — show a retry fallback instead.
    const [loadError, setLoadError] = useState(false);
    const [playerKey, setPlayerKey] = useState(0);
    // Heartbeat throttle: last saved position and a rate-limit cooldown.
    // Refs (not state) so the onTimeUpdate closure never reads stale values.
    const lastSavedRef = useRef(0);
    const backoffUntilRef = useRef(0);
    const updateUserHistory = async (
        videoId: number,
        timestamp: number,
        isCompleted: boolean | null,
    ) => {
        try {
            const res = await fetch("/api/user/video-history", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    videoId,
                    timeStamp: timestamp,
                    isCompleted,
                }),
            });
            // If the edge/server rate-limits us (429), stop the heartbeat until
            // Retry-After elapses — continuing to POST only extends the ban.
            if (res.status === 429) {
                const retry = Number(res.headers.get("retry-after")) || 600;
                backoffUntilRef.current = Date.now() + retry * 1000;
            }
        } catch (error) {
            // Silently fail - video tracking is not critical for video playback
            console.warn("Failed to update video history:", error);
        }
    };
    const handleReady = async (player: {
        getDuration: () => Promise<number>;
        setCurrentTime: (t: number) => Promise<number>;
    }) => {
        // onReady fires per video load; reset the throttle so switching videos
        // starts fresh and we don't immediately re-save the current position.
        lastSavedRef.current = seekTime;
        if (seekTime > 0) {
            try {
                const duration = await player.getDuration();
                if (seekTime < duration) {
                    await player.setCurrentTime(seekTime);
                }
            } catch (error) {
                console.warn("Failed to seek to timestamp:", error);
            }
        }
    };
    if (loadError) {
        return (
            <div className="flex aspect-video w-full flex-col items-center justify-center gap-3 rounded-xl bg-neutral-100 p-6 text-center">
                <p className="font-medium">{t("videoLoadFailed")}</p>
                <p className="text-muted-foreground text-sm">
                    {t("videoLoadFailedHint")}
                </p>
                <Button
                    variant="outline"
                    onClick={() => {
                        setLoadError(false);
                        setPlayerKey((key) => key + 1);
                    }}
                >
                    Tekrar dene
                </Button>
            </div>
        );
    }

    return (
        <Vimeo
            key={playerKey}
            video={video.Location?.replace("/videos/", "") || ""}
            autoplay={autoPlay}
            onError={(error) => {
                console.warn("Vimeo player error:", error);
                setLoadError(true);
            }}
            onEnd={() => {
                updateUserHistory(video.Id, currentTimestamp, true);
                onEnded?.();
            }}
            onPlay={onPlay}
            onPause={onPause}
            responsive={true}
            onReady={handleReady}
            speed={true}
            onTimeUpdate={(event) => {
                const t = Math.round(event.seconds || 0);
                setCurrentTimestamp(t);
                // Save at most once per 10s of progress. Ref-based so rapid
                // onTimeUpdate events (~4/s) can't burst-fire at the boundary
                // the way the old `currentTimestamp % 10` state check did.
                if (
                    t - lastSavedRef.current >= 10 &&
                    Date.now() >= backoffUntilRef.current
                ) {
                    lastSavedRef.current = t;
                    updateUserHistory(video.Id, t, null);
                }
            }}
        />
    );
};
